import React, { useState } from 'react'
import { ZodIssue, ZodIssueCode } from 'zod'
import { PatientHistoryType } from 'src/graphql.generated'
import { INTAKE_NOTE_PSYCHIATRY_FORM_SCHEMA } from 'src/participantHistoryForm/form/IntakeNotePsychiatryFormSchema'
import { useLoadedNoteDraft } from 'src/noteDraft/useNoteDraft'
import { NoteDraftAlerts, NoteDraftLoadError } from 'src/noteDraft/NoteDraftAlerts'
import { useDraftSaver, useRestoredDraft, primeRestoredDraft } from '../../../../api/historyDraft'
import PrefillLoading from '../PrefillLoading'
import { useSubmitPatientHistory } from '../../../../api/useSubmitPatientHistory'
import { IntakeCarePlanNotice, useIntakeCarePlan } from '../../../carePlanEditor/IntakeCarePlanSection'
import { BillingCode } from '../billingCodes'
import BillingCodesSelect from '../BillingCodesSelect'
import {
  FieldError,
  FieldSection,
  FormActions,
  LabeledCheckbox,
  LabeledDate,
  LabeledText,
  MissingFields,
  todayIso,
  useRequiredFields,
} from '../../../fieldPrimitives'
import { boolAt, getIn, textAt } from '../valuePath'
import { PSYCHIATRIC_HISTORY, SOCIAL_AND_FUNCTIONAL_HISTORY, GENERAL, SUMMARY } from '../../historyOptions'

const FIELD_LABELS: Record<string, string> = Object.fromEntries([
  ...GENERAL,
  ['hpi', 'Note'],
  ...PSYCHIATRIC_HISTORY,
  ...SOCIAL_AND_FUNCTIONAL_HISTORY,
  ...SUMMARY,
])

type TooLongIssue = Extract<ZodIssue, { code: typeof ZodIssueCode.too_big }>

const isTooLong = (issue: ZodIssue): issue is TooLongIssue => issue.code === ZodIssueCode.too_big

const tooLongMessage = (issue: TooLongIssue) =>
  `${FIELD_LABELS[String(issue.path[issue.path.length - 1])]} can be at most ${issue.maximum} characters.`

type Texts = Record<string, string>

const textsAt = (source: unknown, prefix: string, fields: readonly (readonly [string, string])[]): Texts =>
  Object.fromEntries(fields.map(([key]) => [key, textAt(source, `${prefix}${key}`)]))

const IntakeNotePsychiatryForm = ({ uid }: { uid: string }) => {
  const restored = useRestoredDraft(uid, PatientHistoryType.IntakeNotePsychiatry)
  const [date, setDate] = useState(textAt(restored, 'date') || todayIso())
  const [visitTypeRemote, setVisitTypeRemote] = useState(boolAt(restored, 'visitTypeRemote', false))
  const [note, setNote] = useState(textAt(restored, 'hpi'))
  // Keep older structured drafts readable by Standard and medical record exports.
  const [legacyTexts] = useState(() => ({
    physicalAppearance: textAt(restored, 'physicalAppearance'),
    ...Object.fromEntries([...GENERAL, ...SUMMARY].map(([key]) => [key, textAt(restored, key)])),
    psychiatricHistory: textsAt(restored, 'psychiatricHistory.', PSYCHIATRIC_HISTORY),
    socialAndFunctionalHistory: textsAt(restored, 'socialAndFunctionalHistory.', SOCIAL_AND_FUNCTIONAL_HISTORY),
  }))
  const [billingCodes, setBillingCodes] = useState<BillingCode[]>(
    Array.isArray(getIn(restored, 'BILLING_CODES')) ? (getIn(restored, 'BILLING_CODES') as BillingCode[]) : []
  )
  const { submit, submitting, error } = useSubmitPatientHistory(uid)
  const carePlan = useIntakeCarePlan(uid)

  const value = {
    date,
    visitTypeRemote,
    ...legacyTexts,
    hpi: note,
    BILLING_CODES: billingCodes,
  }
  useDraftSaver(uid, PatientHistoryType.IntakeNotePsychiatry, value, submitting)
  const validation = INTAKE_NOTE_PSYCHIATRY_FORM_SCHEMA.safeParse(value)
  const tooLong = validation.success ? undefined : validation.error.issues.find(isTooLong)

  const required = useRequiredFields({
    'Date of event': Boolean(date),
    'Visit Type': visitTypeRemote,
    Note: Boolean(note.trim()),
    'Billing codes': billingCodes.length > 0,
  })

  const onSubmit = async () => {
    if (!required.attempt()) {
      return
    }

    try {
      // Keep the logger mounted for the care-plan result and Retry without submitting the note twice.
      await submit({ date, type: PatientHistoryType.IntakeNotePsychiatry, value }, { keepFormOpen: true })
    } catch {
      // `error` from the mutation hook is shown by FormActions.
      return
    }
    await carePlan.create()
  }

  if (carePlan.outcome === 'created' || carePlan.outcome === 'exists') {
    return <IntakeCarePlanNotice plan={carePlan} />
  }

  return (
    <FieldSection>
      <LabeledDate
        label="Date of event"
        value={date}
        onChange={setDate}
        required
        error={required.error('Date of event')}
      />
      <LabeledCheckbox
        label="Visit Type: Synchronous audio-video"
        checked={visitTypeRemote}
        onChange={setVisitTypeRemote}
        required
        error={required.error('Visit Type')}
      />
      <LabeledText
        label="Note"
        value={note}
        onChange={setNote}
        helperText="Paste your scribe's notes here, then review and edit them."
        multiline
        minRows={12}
        required
        error={required.error('Note')}
      />
      <FieldSection title="Billing code and diagnosis">
        <BillingCodesSelect
          value={billingCodes}
          onChange={setBillingCodes}
          required
          error={required.error('Billing codes')}
        />
      </FieldSection>

      <MissingFields labels={required.missing} />
      <FieldError message={tooLong ? tooLongMessage(tooLong) : null} />
      <FormActions
        onSubmit={onSubmit}
        confirm={{ buttonLabel: 'Intake note is complete', title: 'Complete Intake Note' }}
        disabled={Boolean(tooLong) || carePlan.outcome !== null}
        submitting={submitting}
        error={error}
      />
      <IntakeCarePlanNotice plan={carePlan} />
    </FieldSection>
  )
}

const IntakeNotePsychiatryFields = ({ uid }: { uid: string }) => {
  const loaded = useLoadedNoteDraft({ uid, type: PatientHistoryType.IntakeNotePsychiatry })

  if (loaded.failed) {
    return <NoteDraftLoadError />
  }

  if (!loaded.ready) {
    return <PrefillLoading />
  }

  primeRestoredDraft({ uid, type: PatientHistoryType.IntakeNotePsychiatry, value: loaded.draft })

  return (
    <>
      <NoteDraftAlerts uid={uid} formType={PatientHistoryType.IntakeNotePsychiatry} />
      <IntakeNotePsychiatryForm uid={uid} />
    </>
  )
}

export default IntakeNotePsychiatryFields
